/**
 * Revenue Service
 * 매출 데이터 비즈니스 로직 계층
 */
import type { RevenueData, RevenueSummary, PaymentMethodStats } from '../models/Revenue';

export class RevenueService {
  /**
   * 매출 요약 통계 계산
   */
  calculateRevenueSummary(dailyRevenue: RevenueData[]): RevenueSummary {
    if (dailyRevenue.length === 0) {
      return {
        totalRevenue: 0,
        avgDailyRevenue: 0,
        totalMembers: 0,
        avgRevenuePerMember: 0,
        revenueGrowth: 0,
      };
    }

    const totalRevenue = dailyRevenue.reduce((sum, day) => sum + day.revenue, 0);
    const totalMembers = dailyRevenue.reduce((sum, day) => sum + day.members, 0);
    const avgDailyRevenue = Math.floor(totalRevenue / dailyRevenue.length);
    const avgRevenuePerMember = totalMembers > 0 ? Math.floor(totalRevenue / totalMembers) : 0;

    // 전월 대비 매출 성장률 계산 (임시로 90% 가정)
    const lastMonthRevenue = totalRevenue * 0.9;
    const revenueGrowth = lastMonthRevenue > 0
      ? Number(((totalRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(1))
      : 0;

    return {
      totalRevenue,
      avgDailyRevenue,
      totalMembers,
      avgRevenuePerMember,
      revenueGrowth,
    };
  }

  /**
   * 결제 수단별 통계 계산 (임시 데이터)
   */
  calculatePaymentMethodStats(totalRevenue: number): PaymentMethodStats {
    return {
      card: {
        percentage: 65,
        amount: Math.floor(totalRevenue * 0.65),
      },
      cash: {
        percentage: 25,
        amount: Math.floor(totalRevenue * 0.25),
      },
      simple: {
        percentage: 10,
        amount: Math.floor(totalRevenue * 0.10),
      },
    };
  }

  /**
   * 매출 포맷팅 (백만원 단위)
   */
  formatRevenueInMillions(revenue: number): string {
    return (revenue / 1000000).toFixed(1);
  }

  /**
   * 매출 포맷팅 (천원 단위)
   */
  formatRevenueInThousands(revenue: number): string {
    return (revenue / 1000).toFixed(0);
  }

  /**
   * 성장률 부호 판정
   */
  isGrowthPositive(growth: number): boolean {
    return growth > 0;
  }

  /**
   * 성장률 절대값
   */
  getAbsoluteGrowth(growth: number): number {
    return Math.abs(growth);
  }

  /**
   * 기간 라벨 변환
   */
  getPeriodLabel(period: string): string {
    const labels: Record<string, string> = {
      week: '주간',
      month: '월간',
      quarter: '분기',
      year: '연간',
    };
    return labels[period] || period;
  }
}

export const revenueService = new RevenueService();
